import { makeAutoObservable } from "mobx";
import { ZAttrPermission } from "src/business/access/ZAttrAccess";
import { dataParser } from "src/common/dataParser";
import { onError } from "src/common/onError";
import { ZOption } from "src/types";

import { ZSpecAccess, zSpecAccess } from "../../access/ZSpecAccess";
import { ZSpecTypeRaw } from "../ZSpecification";
import {
  EditableAttrAccess,
  EditableSpecAccess,
  specAccessListToEditable,
} from "./EditableSpecAccess";

export const changeColumn = (
  attributes: EditableAttrAccess[],
  mode: ZAttrPermission,
  roleId: string,
) => {
  const onAttr = (attr: EditableAttrAccess) => {
    attr.roles.forEach((role) => {
      if (role.id === roleId) {
        // eslint-disable-next-line no-param-reassign
        role.permission = mode;
      }
    });
    if (attr.attributes) onAttrList(attr.attributes);
  };
  const onAttrList = (list: EditableAttrAccess[]) => list.forEach(onAttr);
  onAttrList(attributes);
};

export class SpecificationsAccessStore {
  constructor(
    src: ZSpecAccess[],
    protected onSave: (specs: ZSpecAccess[]) => Promise<ZSpecAccess[]>,
  ) {
    try {
      this.setSpecList(specAccessListToEditable(src));
    } catch (e) {
      onError(e);
    }
    this.allRoles = buildRolesList(src);
    this.setVisibleRoles(this.allRoles.map(({ value }) => value));
    makeAutoObservable(this);
  }

  specList: EditableSpecAccess[] = [];

  setSpecList(list: EditableSpecAccess[]) {
    this.specList = list;
  }

  getSpecs(needType: ZSpecTypeRaw): EditableSpecAccess[] {
    return this.specList.filter(({ type }) => type === needType);
  }

  allRoles: ZOption[];

  visibleRoles: string[] = [];

  setVisibleRoles(list: string[]) {
    this.visibleRoles = list;
  }

  get visibleColumns(): ZOption[] {
    const { allRoles, visibleRoles } = this;
    if (visibleRoles.length === 0) return allRoles;
    const visibleSet = new Set(visibleRoles);
    return allRoles.filter(({ value }) => visibleSet.has(value));
  }

  saving = false;

  setSaving(value: boolean) {
    this.saving = value;
  }

  async save() {
    try {
      this.setSaving(true);
      const dst = dataParser(
        () => zSpecAccess.array().parse(this.specList),
        "Подготовка данных для сохранения",
      );
      await this.onSave(dst);
      // TODO: Сейчас предполагается, что ответ совпадает с запросом. Поэтому всё остаётся как есть.
      // Если вдруг окажется, что бэк вносит какие-то свои изменения, которые должны отразиться на странице после сохранения,
      // то надо будет вызвать setSpecList, а для этого перевести данные в editable вариант. Но specAccessListToEditable не годится
      // Потому что пропадут всякие коллапсы и уровни. И нужно будет делать функцию, которая тащит эти поля из стараго specList
    } finally {
      this.setSaving(false);
    }
  }
}

const buildRolesList = (specList: ZSpecAccess[]): ZOption[] => {
  const rolesDict: Record<string, string> = {};
  specList.forEach(({ attributes }) =>
    attributes.forEach(({ roles }) =>
      roles.forEach(({ id, label }) => {
        rolesDict[id] = label;
      }),
    ),
  );
  return Object.entries(rolesDict)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};
